import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, Button, Label, Textarea } from '@monksflow/monks-ui';
import { SparklesIcon, TriangleAlertIcon } from 'lucide-react';

import {
  useProposeTrackerAutomationMutation,
  type TrackerAutomationProposal,
} from '@/entities/tracker-automation';

import { automationErrorKey } from '../../lib/automation-errors';

const MAX_SENTENCE_LENGTH = 500;
const MIN_SENTENCE_LENGTH = 3;

type Proposal = TrackerAutomationProposal['proposal'];

/**
 * A rule described in a sentence. The AI's proposal is read back as the
 * rule's summary and saved only through the builder, once the person has
 * opened it there and confirmed it.
 */
export function AutomationDescribeRule({
  trackerId,
  summarize,
  onOpenInBuilder,
}: {
  trackerId: string;
  summarize: (proposal: Proposal) => string;
  onOpenInBuilder: (proposal: Proposal) => void;
}) {
  const { t } = useTranslation('trackers');
  const [sentence, setSentence] = useState('');
  const propose = useProposeTrackerAutomationMutation({ trackerId });
  const proposal = propose.data?.proposal;
  const ready = sentence.trim().length >= MIN_SENTENCE_LENGTH;

  const errorKey = propose.isError
    ? automationErrorKey(propose.error, 'trackersBoard.automationAi.describe.failed')
    : null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        {t('trackersBoard.automationAi.describe.intro')}
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="automation-describe">
          {t('trackersBoard.automationAi.describe.label')}
        </Label>
        <Textarea
          id="automation-describe"
          rows={3}
          maxLength={MAX_SENTENCE_LENGTH}
          placeholder={t('trackersBoard.automationAi.describe.placeholder')}
          value={sentence}
          onChange={(event) => {
            setSentence(event.target.value);
            if (propose.data || propose.isError) propose.reset();
          }}
        />
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="self-start"
        disabled={!ready || propose.isPending}
        onClick={() => propose.mutate({ sentence: sentence.trim() })}
      >
        <SparklesIcon aria-hidden />
        {propose.isPending
          ? t('trackersBoard.automationAi.describe.proposing')
          : t('trackersBoard.automationAi.describe.propose')}
      </Button>
      {errorKey ? (
        <Alert role="alert">
          <TriangleAlertIcon aria-hidden />
          <AlertDescription>{t(errorKey)}</AlertDescription>
        </Alert>
      ) : null}
      {proposal ? (
        <div className="flex flex-col gap-2" role="status">
          <p className="text-xs font-medium text-muted-foreground">
            {t('trackersBoard.automationAi.describe.review')}
          </p>
          <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
            {summarize(proposal)}
          </p>
          <Button
            type="button"
            size="sm"
            className="self-start"
            onClick={() => onOpenInBuilder(proposal)}
          >
            {t('trackersBoard.automationAi.describe.openInBuilder')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
